/*
 * DataView — interactive renderer for `data_view` JSON fences emitted
 * by Splash. Mirrors the existing tool_call / confirm_action fence
 * pattern: model produces structured JSON, SPA owns the pixels.
 *
 * Three view types in v1:
 *   - type: "table"  — sortable columns, datetime-aware cells
 *   - type: "chart"  — line or bar via Chart.js + react-chartjs-2
 *   - type: "kv"     — Bootstrap dl.row label/value layout
 *
 * Schema is validated with hand-rolled type guards (no zod dep) — bad
 * specs render a small error pill instead of crashing the bubble.
 *
 * Datetime columns and chart x-axes go through `formatLocalTime` /
 * `formatChartTick` so timestamps display in the user's local tz, not
 * UTC. The model is told (in the system prompt) to keep timestamps as
 * ISO-UTC strings on the wire and let the SPA format them.
 *
 * See Docs/plans/PLAN_SPLASH_DATA_VIEWS.md for the spec rationale and
 * the order-preserving splice strategy that calls into this module.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  type ChartOptions,
} from 'chart.js';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import { formatLocalTime, formatChartTick, getUserLocale } from '../lib/userTz';

// ── Chart.js one-time registration ─────────────────────────────────
// Register everything we need on module load. Tree-shaking still drops
// unregistered chart types from the bundle.
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

// ── Spec types ─────────────────────────────────────────────────────

export type ColumnKind = 'datetime' | 'number' | 'string';

export interface TableColumn {
  key: string;
  label: string;
  kind: ColumnKind;
  align?: 'left' | 'right' | 'center';
}

export interface TableSpec {
  type: 'table';
  title?: string;
  columns: TableColumn[];
  rows: Record<string, unknown>[];
  defaultSort?: { key: string; dir: 'asc' | 'desc' };
  /** If rows.length > maxRows, render a "Show all (N)" disclosure. Default 25. */
  maxRows?: number;
  footnote?: string;
}

export interface ChartSeries {
  name: string;
  yKey: string;
  data: Record<string, unknown>[];
  /**
   * Overlay a derived trend line on top of this series.
   *   - "linear"  → least-squares best-fit line over the y values
   *   - "moving"  → simple moving average with `trendWindow` size
   *                 (defaults to ~1/8 of the point count, min 3)
   * Splash is told (in the system prompt) to set this when the user
   * asks for a trend / says something is "going up", etc.
   */
  trend?: 'linear' | 'moving';
  trendWindow?: number;
}

export interface ChartSpec {
  type: 'chart';
  /**
   *   - "line"     → time-series / continuous trend
   *   - "bar"      → categorical comparison
   *   - "pie"      → composition / share of whole (one series only)
   *   - "doughnut" → same as pie, with a hole (one series only)
   */
  kind: 'line' | 'bar' | 'pie' | 'doughnut';
  title?: string;
  /** Label key. For pie/doughnut, names the slice; the first series'
   * data array becomes the slices. */
  xKey: string;
  xKind?: ColumnKind;
  series: ChartSeries[];
  yLabel?: string;
  footnote?: string;
}

export interface KvItem {
  label: string;
  value: unknown;
  kind?: ColumnKind;
}

export interface KvSpec {
  type: 'kv';
  title?: string;
  items: KvItem[];
}

export type DataViewSpec = TableSpec | ChartSpec | KvSpec;

// ── Type guards ────────────────────────────────────────────────────

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isTableSpec(v: unknown): v is TableSpec {
  if (!isObject(v) || v.type !== 'table') return false;
  if (!Array.isArray(v.columns) || !Array.isArray(v.rows)) return false;
  return v.columns.every(
    (c) =>
      isObject(c) &&
      typeof c.key === 'string' &&
      typeof c.label === 'string' &&
      (c.kind === 'datetime' || c.kind === 'number' || c.kind === 'string'),
  );
}

function isChartSpec(v: unknown): v is ChartSpec {
  if (!isObject(v) || v.type !== 'chart') return false;
  if (
    v.kind !== 'line' &&
    v.kind !== 'bar' &&
    v.kind !== 'pie' &&
    v.kind !== 'doughnut'
  ) {
    return false;
  }
  if (typeof v.xKey !== 'string') return false;
  if (!Array.isArray(v.series)) return false;
  return v.series.every(
    (s) =>
      isObject(s) &&
      typeof s.name === 'string' &&
      typeof s.yKey === 'string' &&
      Array.isArray(s.data),
  );
}

function isKvSpec(v: unknown): v is KvSpec {
  if (!isObject(v) || v.type !== 'kv') return false;
  if (!Array.isArray(v.items)) return false;
  return v.items.every((i) => isObject(i) && typeof i.label === 'string');
}

export function validateDataViewSpec(spec: unknown): DataViewSpec | null {
  if (isTableSpec(spec)) return spec;
  if (isChartSpec(spec)) return spec;
  if (isKvSpec(spec)) return spec;
  return null;
}

// ── Fence parser ───────────────────────────────────────────────────

const DATA_VIEW_FENCE_RE = /```data_view\s*\n([\s\S]*?)\n```/g;

export interface DataViewMatch {
  /** Position in the source string where the fence starts. */
  start: number;
  /** Position one past the end of the closing fence. */
  end: number;
  /** Validated spec, or null if JSON parse / validation failed. */
  spec: DataViewSpec | null;
  /** Raw JSON body for fallback display when validation fails. */
  raw: string;
}

export function findDataViewFences(content: string): DataViewMatch[] {
  const out: DataViewMatch[] = [];
  // Reset the global regex state — DATA_VIEW_FENCE_RE is shared.
  DATA_VIEW_FENCE_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = DATA_VIEW_FENCE_RE.exec(content)) !== null) {
    const body = m[1] ?? '';
    let spec: DataViewSpec | null = null;
    try {
      spec = validateDataViewSpec(JSON.parse(body));
    } catch {
      spec = null;
    }
    out.push({
      start: m.index,
      end: m.index + m[0].length,
      spec,
      raw: body,
    });
  }
  return out;
}

// ── Cell formatters ────────────────────────────────────────────────

function formatNumber(v: unknown): string {
  if (typeof v !== 'number' || !Number.isFinite(v)) return String(v ?? '');
  return new Intl.NumberFormat(getUserLocale()).format(v);
}

function formatCell(value: unknown, kind: ColumnKind): string {
  if (value === null || value === undefined) return '';
  if (kind === 'datetime') return formatLocalTime(value as string);
  if (kind === 'number') return formatNumber(value);
  return String(value);
}

function compareCell(a: unknown, b: unknown, kind: ColumnKind): number {
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;
  if (kind === 'number') {
    return (Number(a) || 0) - (Number(b) || 0);
  }
  if (kind === 'datetime') {
    const ta = new Date(a as string).getTime();
    const tb = new Date(b as string).getTime();
    return (Number.isNaN(ta) ? 0 : ta) - (Number.isNaN(tb) ? 0 : tb);
  }
  return String(a).localeCompare(String(b));
}

// ── TableView ──────────────────────────────────────────────────────

function TableView({ spec }: { spec: TableSpec }) {
  const maxRows = spec.maxRows ?? 25;
  const [showAll, setShowAll] = useState(false);
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(
    spec.defaultSort ?? null,
  );

  const sortedRows = useMemo(() => {
    if (!sort) return spec.rows;
    const col = spec.columns.find((c) => c.key === sort.key);
    if (!col) return spec.rows;
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [...spec.rows].sort(
      (a, b) => sign * compareCell(a[col.key], b[col.key], col.kind),
    );
  }, [spec.rows, spec.columns, sort]);

  const visibleRows = showAll ? sortedRows : sortedRows.slice(0, maxRows);
  const hidden = sortedRows.length - visibleRows.length;

  const onHeaderClick = (key: string) => {
    setSort((cur) => {
      if (!cur || cur.key !== key) return { key, dir: 'asc' };
      return cur.dir === 'asc' ? { key, dir: 'desc' } : null;
    });
  };

  return (
    <div style={{ margin: '8px 0' }}>
      {spec.title ? (
        <div
          style={{
            fontWeight: 600,
            fontSize: '0.9rem',
            marginBottom: 4,
          }}
        >
          {spec.title}
        </div>
      ) : null}
      <div style={{ overflowX: 'auto' }}>
        <table
          className="table table-sm table-bordered table-hover"
          style={{ fontSize: '0.85rem', marginBottom: 0 }}
        >
          <thead>
            <tr>
              {spec.columns.map((col) => {
                const isSorted = sort?.key === col.key;
                const arrow = isSorted ? (sort?.dir === 'asc' ? ' ▲' : ' ▼') : '';
                return (
                  <th
                    key={col.key}
                    onClick={() => onHeaderClick(col.key)}
                    style={{
                      cursor: 'pointer',
                      userSelect: 'none',
                      background: 'rgba(0,0,0,0.04)',
                      whiteSpace: 'nowrap',
                      textAlign: col.align ?? (col.kind === 'number' ? 'right' : 'left'),
                    }}
                    title="Click to sort"
                  >
                    {col.label}
                    {arrow}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, ri) => (
              <tr key={ri}>
                {spec.columns.map((col) => (
                  <td
                    key={col.key}
                    style={{
                      verticalAlign: 'top',
                      textAlign: col.align ?? (col.kind === 'number' ? 'right' : 'left'),
                      whiteSpace: col.kind === 'datetime' ? 'nowrap' : 'normal',
                    }}
                  >
                    {formatCell(row[col.key], col.kind)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hidden > 0 ? (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          style={{
            marginTop: 4,
            background: 'transparent',
            border: 'none',
            color: 'var(--aqua-primary, #0ea5e9)',
            padding: 0,
            fontSize: '0.85rem',
            cursor: 'pointer',
          }}
        >
          Show all ({sortedRows.length})
        </button>
      ) : null}
      {spec.footnote ? (
        <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: 4 }}>
          {spec.footnote}
        </div>
      ) : null}
    </div>
  );
}

// ── ChartView ──────────────────────────────────────────────────────

/**
 * Pull a few theme tokens from CSS vars so charts pick up
 * light/dark-mode shifts. Falls back to a pool-themed default
 * palette if vars aren't defined.
 */
function readChartPalette(): { primary: string; palette: string[] } {
  let primary = '#0ea5e9';
  if (typeof window !== 'undefined' && document?.documentElement) {
    const v = getComputedStyle(document.documentElement)
      .getPropertyValue('--aqua-primary')
      ?.trim();
    if (v) primary = v;
  }
  const palette = [primary, '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
  return { primary, palette };
}

function PieView({ spec, isDoughnut }: { spec: ChartSpec; isDoughnut: boolean }) {
  const paletteRef = useRef<ReturnType<typeof readChartPalette> | null>(null);
  if (!paletteRef.current) paletteRef.current = readChartPalette();
  const { palette } = paletteRef.current;

  // Pie expects ONE series whose rows become slices. If the model sent
  // multiple series, take the first and surface a footnote.
  const primary = spec.series[0];
  const sliceLabels = useMemo(
    () => (primary?.data ?? []).map((row) => String(row[spec.xKey] ?? '')),
    [primary, spec.xKey],
  );
  const sliceValues = useMemo(
    () =>
      (primary?.data ?? []).map((row) =>
        Number((primary && row[primary.yKey]) ?? 0),
      ),
    [primary],
  );
  const sliceColors = sliceLabels.map(
    (_, i) => palette[i % palette.length] ?? palette[0]!,
  );

  const data = {
    labels: sliceLabels,
    datasets: [
      {
        label: primary?.name ?? '',
        data: sliceValues,
        backgroundColor: sliceColors,
        borderColor: 'rgba(255,255,255,0.6)',
        borderWidth: 1,
      },
    ],
  } as unknown;

  const options: ChartOptions<'pie' | 'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'right', labels: { boxWidth: 12, font: { size: 11 } } },
      tooltip: { enabled: true },
      title: spec.title
        ? { display: true, text: spec.title, font: { size: 13 } }
        : undefined,
    },
  };

  const extraNote =
    spec.series.length > 1
      ? '(showing first series; pie charts render one series only)'
      : null;

  return (
    <div style={{ margin: '8px 0' }}>
      <div style={{ height: 220, position: 'relative' }}>
        {isDoughnut ? (
          <Doughnut
            data={data as React.ComponentProps<typeof Doughnut>['data']}
            options={options as ChartOptions<'doughnut'>}
          />
        ) : (
          <Pie
            data={data as React.ComponentProps<typeof Pie>['data']}
            options={options as ChartOptions<'pie'>}
          />
        )}
      </div>
      {(spec.footnote || extraNote) ? (
        <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: 4 }}>
          {[spec.footnote, extraNote].filter(Boolean).join(' ')}
        </div>
      ) : null}
    </div>
  );
}

function ChartView({ spec }: { spec: ChartSpec }) {
  // Pie/doughnut take a different data shape — split them out.
  // The non-pie path needs Chart.js hooks (useRef/useEffect/useMemo).
  // Routing through a separate sub-component keeps the per-component
  // hook count stable — react-hooks/rules-of-hooks requires that the
  // number and order of hooks called per render is constant, and an
  // early return before hooks (as the prior shape had) violates that
  // even though it was safe in practice today thanks to parent keying.
  if (spec.kind === 'pie' || spec.kind === 'doughnut') {
    return <PieView spec={spec} isDoughnut={spec.kind === 'doughnut'} />;
  }
  return <NonPieChartView spec={spec} />;
}

function NonPieChartView({ spec }: { spec: ChartSpec }) {
  // Re-read palette on remount so dark-mode toggles refresh colors.
  // useRef caches across renders to avoid a getComputedStyle hit per
  // tooltip event.
  const paletteRef = useRef<ReturnType<typeof readChartPalette> | null>(null);
  if (!paletteRef.current) paletteRef.current = readChartPalette();

  // Nuke palette cache when the doc theme attribute flips. Cheap.
  useEffect(() => {
    const root = document?.documentElement;
    if (!root) return;
    const obs = new MutationObserver(() => {
      paletteRef.current = readChartPalette();
    });
    obs.observe(root, { attributes: true, attributeFilter: ['data-theme', 'class'] });
    return () => obs.disconnect();
  }, []);

  const { palette } = paletteRef.current;

  // x-axis labels. If xKind is 'datetime', format in user tz.
  const xKind = spec.xKind ?? 'string';
  const labels = useMemo(() => {
    // Take the first series' x values as the axis spine. All series
    // are assumed aligned on x; if they aren't, the model should have
    // sent them as separate charts.
    const firstSeries = spec.series[0]?.data ?? [];
    return firstSeries.map((row) => {
      const xv = row[spec.xKey];
      if (xKind === 'datetime') return formatChartTick(xv as string);
      if (xKind === 'number') return formatNumber(xv);
      return String(xv ?? '');
    });
  }, [spec.series, spec.xKey, xKind]);

  const datasets = useMemo(() => {
    const out: Array<Record<string, unknown>> = [];
    spec.series.forEach((s, i) => {
      const color = palette[i % palette.length] ?? palette[0]!;
      const yValues = s.data.map((row) => Number(row[s.yKey] ?? 0));
      // Primary series.
      out.push({
        label: s.name,
        data: yValues,
        borderColor: color,
        backgroundColor: spec.kind === 'line' ? hexAlpha(color, 0.2) : color,
        fill: spec.kind === 'line',
        tension: 0.25,
        pointRadius: spec.kind === 'line' && labels.length > 50 ? 0 : 2,
        borderWidth: 2,
      });
      // Optional trend overlay. Only meaningful on line charts; for bar
      // charts we still render but as a thin line on top.
      if (s.trend === 'linear' && yValues.length >= 2) {
        out.push({
          type: 'line' as const,
          label: `${s.name} (linear trend)`,
          data: linearRegressionFit(yValues),
          borderColor: hexAlpha(color, 0.65),
          backgroundColor: 'transparent',
          fill: false,
          borderDash: [6, 4],
          borderWidth: 1.5,
          pointRadius: 0,
          tension: 0,
        });
      } else if (s.trend === 'moving' && yValues.length >= 3) {
        const w = s.trendWindow ?? Math.max(3, Math.floor(yValues.length / 8));
        out.push({
          type: 'line' as const,
          label: `${s.name} (${w}-pt moving avg)`,
          data: movingAverageFit(yValues, w),
          borderColor: hexAlpha(color, 0.65),
          backgroundColor: 'transparent',
          fill: false,
          borderDash: [6, 4],
          borderWidth: 1.5,
          pointRadius: 0,
          tension: 0.3,
          spanGaps: false,
        });
      }
    });
    return out;
  }, [spec.series, spec.kind, palette, labels.length]);

  // Datasets are typed loosely because we mix per-dataset shape options
  // (trend overlays may be a different chart type than the primary
  // series). Chart.js accepts the mix at runtime; the typed React
  // wrappers want a strict ChartData<'line'|'bar', ...> shape.
  const data = { labels, datasets } as unknown;
  const options: ChartOptions<'line' | 'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: spec.series.length > 1,
        labels: { boxWidth: 12, font: { size: 11 } },
      },
      tooltip: { mode: 'index', intersect: false },
      title: spec.title
        ? { display: true, text: spec.title, font: { size: 13 } }
        : undefined,
    },
    scales: {
      x: {
        ticks: {
          maxTicksLimit: 8,
          font: { size: 10 },
          autoSkip: true,
        },
      },
      y: {
        title: spec.yLabel ? { display: true, text: spec.yLabel } : undefined,
        ticks: { font: { size: 10 } },
      },
    },
    interaction: { mode: 'index', intersect: false },
  };

  return (
    <div style={{ margin: '8px 0' }}>
      {spec.title && spec.series.length === 1 ? (
        <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 4 }}>
          {spec.title}
        </div>
      ) : null}
      <div style={{ height: 200, position: 'relative' }}>
        {spec.kind === 'line' ? (
          <Line
            data={data as React.ComponentProps<typeof Line>['data']}
            options={options as ChartOptions<'line'>}
          />
        ) : (
          <Bar
            data={data as React.ComponentProps<typeof Bar>['data']}
            options={options as ChartOptions<'bar'>}
          />
        )}
      </div>
      {spec.footnote ? (
        <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: 4 }}>
          {spec.footnote}
        </div>
      ) : null}
    </div>
  );
}

// ── Trend math ─────────────────────────────────────────────────────

/**
 * Least-squares linear regression over y values (x is the point index).
 * Returns one fit point per input value. Used for trend-line overlay.
 */
function linearRegressionFit(values: number[]): number[] {
  const n = values.length;
  if (n < 2) return values.slice();
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumX2 = 0;
  for (let i = 0; i < n; i++) {
    const y = values[i] ?? 0;
    sumX += i;
    sumY += y;
    sumXY += i * y;
    sumX2 += i * i;
  }
  const denom = n * sumX2 - sumX * sumX;
  if (denom === 0) return values.slice();
  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  const out: number[] = new Array(n);
  for (let i = 0; i < n; i++) out[i] = slope * i + intercept;
  return out;
}

/**
 * Trailing simple moving average. First (window-1) entries are null
 * (Chart.js renders gaps for null with spanGaps:false).
 */
function movingAverageFit(values: number[], window: number): (number | null)[] {
  const n = values.length;
  const w = Math.max(1, Math.min(window, n));
  const out: (number | null)[] = new Array(n);
  for (let i = 0; i < n; i++) {
    if (i < w - 1) {
      out[i] = null;
    } else {
      let s = 0;
      for (let j = 0; j < w; j++) s += values[i - j] ?? 0;
      out[i] = s / w;
    }
  }
  return out;
}

/** Convert "#rrggbb" or "#rgb" to rgba with the given alpha. */
function hexAlpha(hex: string, alpha: number): string {
  const h = hex.trim().replace(/^#/, '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return hex;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ── KvView ─────────────────────────────────────────────────────────

function KvView({ spec }: { spec: KvSpec }) {
  return (
    <div style={{ margin: '8px 0' }}>
      {spec.title ? (
        <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 4 }}>
          {spec.title}
        </div>
      ) : null}
      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'max-content 1fr',
          columnGap: 12,
          rowGap: 4,
          margin: 0,
          fontSize: '0.85rem',
        }}
      >
        {spec.items.map((item, i) => (
          <div
            key={i}
            style={{ display: 'contents' }}
          >
            <dt style={{ fontWeight: 500, opacity: 0.75 }}>{item.label}</dt>
            <dd style={{ margin: 0 }}>
              {formatCell(item.value, item.kind ?? 'string')}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// ── Error placeholder ──────────────────────────────────────────────

function DataViewError({ message, raw }: { message: string; raw?: string }) {
  return (
    <div
      style={{
        margin: '8px 0',
        padding: '6px 10px',
        border: '1px solid rgba(220,53,69,0.4)',
        background: 'rgba(220,53,69,0.08)',
        borderRadius: 6,
        fontSize: '0.8rem',
        color: '#dc3545',
      }}
    >
      <strong>data_view error:</strong> {message}
      {raw ? (
        <details style={{ marginTop: 4, fontSize: '0.75rem' }}>
          <summary style={{ cursor: 'pointer' }}>show raw spec</summary>
          <pre
            style={{
              margin: '4px 0 0 0',
              padding: 6,
              background: 'rgba(0,0,0,0.04)',
              borderRadius: 4,
              maxHeight: 160,
              overflow: 'auto',
            }}
          >
            {raw}
          </pre>
        </details>
      ) : null}
    </div>
  );
}

// ── Public dispatcher ──────────────────────────────────────────────

export function DataView({
  spec,
  raw,
}: {
  spec: DataViewSpec | null;
  raw?: string;
}) {
  if (!spec) {
    return (
      <DataViewError
        message="spec failed validation (unknown type or missing required fields)"
        raw={raw}
      />
    );
  }
  switch (spec.type) {
    case 'table':
      return <TableView spec={spec} />;
    case 'chart':
      return <ChartView spec={spec} />;
    case 'kv':
      return <KvView spec={spec} />;
    default:
      // Exhaustiveness — TS will complain if a new type is added without
      // a case here.
      return <DataViewError message="unhandled view type" raw={raw} />;
  }
}
